import { supabase } from './supabase';
import { firebaseAuth } from './firebase';

/* ============================================================
   EzyDine data layer — every read/write goes through Supabase
   (schema `ezydine`, camelCase columns). Auth identity comes
   from Firebase; RLS scopes rows to the merchant's restaurant.
   ============================================================ */

/* ---------------- Types (DB row shapes) ---------------- */

export interface Restaurant {
  id: string;
  name: string;
  slug: string;
  address: string | null;
  phone: string | null;
  gstin: string | null;
  fssai: string | null;
  gstRate: number;
  /** When true, menu prices already INCLUDE GST (tax is extracted from the
      price). When false/undefined, GST is added ON TOP of the price (default,
      preserves existing outlets' bills). Stored in settings.gstInclusive. */
  gstInclusive?: boolean;
  billFooter: string | null;
  ownerPin: string | null;
  settings: Record<string, unknown> | null;
  createdAt?: string;
}

/** GST breakup for a menu-price subtotal (rupees).
    - exclusive (default): tax is added on top → total = subtotal + cgst + sgst.
    - inclusive: menu prices already contain GST → total = subtotal, and the
      tax shown is the portion extracted out of the price.
    In both cases cgst + sgst + taxable === total. */
export interface GstBreakup {
  taxable: number; // net value excluding GST
  cgst: number;
  sgst: number;
  total: number; // amount the guest pays
  inclusive: boolean;
}

export function computeGst(subtotal: number, gstRate: number, inclusive: boolean): GstBreakup {
  if (inclusive) {
    const gst = Math.round((subtotal * gstRate * 100) / (100 + gstRate)) / 100;
    const cgst = Math.round((gst * 100) / 2) / 100;
    const sgst = Math.round((gst - cgst) * 100) / 100;
    return { taxable: Math.round((subtotal - gst) * 100) / 100, cgst, sgst, total: subtotal, inclusive: true };
  }
  const cgst = Math.round(subtotal * (gstRate / 2)) / 100;
  const sgst = cgst;
  return { taxable: subtotal, cgst, sgst, total: subtotal + cgst + sgst, inclusive: false };
}

export interface Category {
  id: string;
  restaurantId: string;
  name: string;
  icon: string | null;
  itemCount: number;
  sortOrder: number;
}

export interface MenuVariant { name: string; price: number; }
export interface MenuAddon { name: string; price: number; }

export interface MenuItem {
  id: string;
  restaurantId: string;
  name: string;
  category: string | null;
  description: string | null;
  price: number;
  foodType: 'veg' | 'non-veg' | 'egg';
  prepTime: number;
  gstRate: number;
  hsnCode: string;
  available: boolean;
  recommended: boolean;
  stockQty: number;
  image: string | null;
  variants: MenuVariant[];
  addons: MenuAddon[];
  dineIn: boolean;
  takeaway: boolean;
  online: boolean;
}

export interface RestaurantTable {
  id: string;
  restaurantId: string;
  label: string | null;
  seats: number;
  zone: string | null;
  slug: string | null;
  qrUrl: string | null;
  orderingEnabled: boolean;
  status: string;
  seatedMinutes: number | null;
  amount: number | null;
  kotMins: number | null;
}

export interface Order {
  id: string;
  restaurantId: string;
  type: string;
  tableId: string | null;
  customer: string;
  itemCount: number;
  subtotal: number;
  cgst: number;
  sgst: number;
  total: number;
  status: string;
  paymentMode: string;
  paymentStatus: string;
  placedAt: string;
  completedAt: string | null;
  customerPhone?: string | null;
  tokenNumber?: number | null;
  billNo?: string | null;
  // Void bookkeeping (see supabase/ezydine-void-retention.sql). A voided bill
  // has status 'cancelled' AND voidedAt set; it's hidden from the POS and lives
  // in the recycle bin until purged ~60 days after voidedAt.
  voidedAt?: string | null;
  voidedBy?: string | null;
  voidReason?: string | null;
  irn?: string | null;
  irnStatus?: string | null;
  irnSignedQr?: string | null;
  irnAckNo?: string | null;
  irnError?: string | null;
}

export interface OrderItem {
  id: string;
  orderId: string;
  restaurantId: string;
  name: string;
  qty: number;
  price: number;
  modifiers: string[];
  specialInstructions: string | null;
}

export interface Customer {
  id: string;
  restaurantId: string;
  name: string;
  phone: string | null;
  email: string | null;
  totalVisits: number;
  totalSpend: number;
  avgOrderValue: number;
  lastVisit: string | null;
  segment: 'New' | 'Regular' | 'VIP' | 'Lapsed';
  loyaltyPoints: number;
  walletBalance?: number;
  dietaryPrefs: string[];
  joinedDate: string;
  tags: string[];
}

export interface StaffMember {
  id: string;
  restaurantId: string;
  name: string;
  role: string;
  phone: string | null;
  email: string | null;
  pin: string | null;
  pinHash?: string | null;
  shift: string;
  status: string;
  joinedDate: string;
  lastActive: string | null;
  permissions: string[];
  active: boolean;
}

export interface AuditEntry {
  id: string;
  restaurantId: string;
  type: string;
  detail: string;
  reason: string | null;
  by: string | null;
  staffId: string | null;
  at: string;
}

export interface InventoryItem {
  id: string;
  restaurantId: string;
  name: string;
  category: string | null;
  unit: string;
  currentStock: number;
  reorderPoint: number;
  optimalStock: number;
  unitCost: number;
  lastRestocked: string | null;
  supplier: string | null;
  wastageYtd: number;
  shelfLife: string | null;
}

export interface WastageEntry {
  id: string;
  restaurantId: string;
  itemName: string;
  quantity: number;
  unit: string | null;
  reason: string | null;
  value: number;
  recordedBy: string | null;
  recordedAt: string;
}

/* ---------------- Helpers ---------------- */

export function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

/** Stable-ish text id for tables/categories/menu items (text PKs). */
export function makeId(name: string): string {
  return `${slugify(name) || 'item'}-${Math.random().toString(36).slice(2, 6)}`;
}

function need<T>(data: T | null, error: { message: string } | null): T {
  if (error) throw new Error(error.message);
  if (data === null) throw new Error('Not found.');
  return data;
}

/* ---------------- Onboarding ---------------- */

/** Create the restaurant, link the signed-in user as owner, and seed
 *  starter categories + tables (with QR links). Returns the row. */
export async function createRestaurant(params: {
  name: string;
  ownerName?: string;
  ownerEmail?: string;
  ownerPhone?: string;
  address?: string;
  gstin?: string;
  fssai?: string;
  gstRate?: number;
  tableCount?: number;
}): Promise<Restaurant> {
  const user = firebaseAuth.currentUser;
  if (!user) throw new Error('Please sign in first.');

  // Clean, professional slug: "Garden Kitchen" -> gardenkitchen
  // (used as ezydinepro.com/{slug}). Reserved app paths are avoided;
  // only if the name is already taken do we append -2, -3, ...
  const RESERVED = new Set(['admin', 'dashboard', 'superadmin', 'login', 'onboarding', 'r', 't', 'assets', 'api']);
  let base = (slugify(params.name) || 'restaurant').replace(/-/g, '');
  if (RESERVED.has(base)) base = `${base}restaurant`;
  // Check slug availability via the security-definer RPC. A direct
  // `from('restaurants')` select is hidden by RLS (you can't see other
  // tenants' rows), so it would always look "free" and then collide on insert.
  let slug = base;
  for (let i = 2; ; i++) {
    const { data: rows } = await supabase.rpc('restaurant_public', { p_slug: slug });
    const taken = Array.isArray(rows) ? rows[0] : rows;
    if (!taken) break;
    slug = `${base}-${i}`;
  }

  // 1. restaurant — generate the id on the client so we DON'T need to read the
  // row back here. RLS only makes the restaurant readable once the merchant
  // link (step 2) exists (via my_restaurant()), so a .select() at this point
  // would return nothing and fail. We already know every field, so build the
  // Restaurant object locally instead.
  const newId = crypto.randomUUID();
  const { error } = await supabase.from('restaurants').insert({
    id: newId,
    name: params.name,
    slug,
    phone: params.ownerPhone ?? user.phoneNumber ?? null,
    email: params.ownerEmail ?? user.email ?? null,
    address: params.address ?? null,
    gstin: params.gstin ?? null,
    fssai: params.fssai ?? null,
    gstRate: params.gstRate ?? 5,
  });
  if (error) throw error;
  const r: Restaurant = {
    id: newId,
    name: params.name,
    slug,
    address: params.address ?? null,
    phone: params.ownerPhone ?? user.phoneNumber ?? null,
    gstin: params.gstin ?? null,
    fssai: params.fssai ?? null,
    gstRate: params.gstRate ?? 5,
    billFooter: null,
    ownerPin: null,
    settings: null,
  };

  // 2. merchant (links auth user -> restaurant; enables tenant RLS)
  const { error: mErr } = await supabase.from('merchants').upsert({
    id: user.uid,
    restaurantId: r.id,
    name: params.ownerName ?? null,
    email: params.ownerEmail ?? user.email ?? null,
    phone: params.ownerPhone ?? user.phoneNumber ?? null,
    role: 'owner',
  });
  if (mErr) throw mErr;

  // 3. starter categories
  const cats: [string, string][] = [
    ['starters', 'Starters'], ['mains', 'Main Course'], ['breads', 'Breads'],
    ['rice', 'Rice & Biryani'], ['beverages', 'Beverages'], ['desserts', 'Desserts'],
  ];
  const { error: cErr } = await supabase.from('categories').insert(
    cats.map(([id, name], i) => ({ id, restaurantId: r.id, name, sortOrder: i + 1 })),
  );
  if (cErr) throw cErr;

  // 4. tables + QR links
  const n = params.tableCount ?? 8;
  const tables = Array.from({ length: n }, (_, i) => {
    const code = `T${i + 1}`;
    return {
      id: code,
      restaurantId: r.id,
      label: `Table ${i + 1}`,
      seats: 4,
      zone: 'Main Hall',
      slug: code,
      qrUrl: `/t/${slug}/${code}`,
      orderingEnabled: true,
      status: 'free',
    };
  });
  const { error: tErr } = await supabase.from('restaurant_tables').insert(tables);
  if (tErr) throw tErr;

  return r as Restaurant;
}

/* ---------------- Restaurant / settings ---------------- */

export async function fetchRestaurant(id: string): Promise<Restaurant> {
  const { data, error } = await supabase.from('restaurants').select('*').eq('id', id).single();
  return need(data, error) as Restaurant;
}

export async function updateRestaurant(id: string, patch: Partial<Omit<Restaurant, 'id'>>): Promise<Restaurant> {
  const { data, error } = await supabase.from('restaurants').update(patch).eq('id', id).select().single();
  return need(data, error) as Restaurant;
}

/* ---------------- WhatsApp (WABA) config + log ---------------- */

export interface WhatsAppConfig {
  enabled: boolean;
  phoneNumberId: string;
  accessToken: string;
  languageCode: string;
  events: { placed: boolean; ready: boolean; bill: boolean };
}

export const defaultWhatsAppConfig: WhatsAppConfig = {
  enabled: false,
  phoneNumberId: '',
  accessToken: '',
  languageCode: 'en',
  events: { placed: true, ready: true, bill: false },
};

export function getWhatsAppConfig(restaurant: Restaurant): WhatsAppConfig {
  const raw = ((restaurant.settings ?? {}) as Record<string, unknown>).whatsapp as
    | Partial<WhatsAppConfig>
    | undefined;
  return {
    ...defaultWhatsAppConfig,
    ...(raw ?? {}),
    events: { ...defaultWhatsAppConfig.events, ...(raw?.events ?? {}) },
  };
}

/** Persist WhatsApp config by merging into restaurants.settings (jsonb). */
export async function saveWhatsAppConfig(
  restaurant: Restaurant,
  cfg: WhatsAppConfig,
): Promise<Restaurant> {
  const settings = { ...((restaurant.settings ?? {}) as Record<string, unknown>), whatsapp: cfg };
  return updateRestaurant(restaurant.id, { settings });
}

export interface WhatsAppLogEntry {
  id: string;
  restaurantId: string;
  orderId: string | null;
  toPhone: string;
  event: string;
  status: string;
  error: string | null;
  createdAt: string;
}

export async function fetchWhatsAppLog(restaurantId: string, limit = 50): Promise<WhatsAppLogEntry[]> {
  const { data, error } = await supabase
    .from('whatsapp_log')
    .select('*')
    .eq('restaurantId', restaurantId)
    .order('createdAt', { ascending: false })
    .limit(limit);
  return need(data, error) as WhatsAppLogEntry[];
}

/* ---------------- Loyalty program ---------------- */

export interface LoyaltyTier {
  name: string;
  minPoints: number;
}

export interface LoyaltyConfig {
  enabled: boolean;
  earnRate: number;        // points earned per ₹100 spent
  redeemValue: number;     // ₹ value of 1 point
  minRedeemPoints: number; // minimum balance before redeeming
  maxRedeemPct: number;    // cap redemption at this % of the bill
  tiers: LoyaltyTier[];    // ordered low→high; tier = highest minPoints met
}

export const defaultLoyaltyConfig: LoyaltyConfig = {
  enabled: false,
  earnRate: 1,
  redeemValue: 1,
  minRedeemPoints: 100,
  maxRedeemPct: 20,
  tiers: [
    { name: 'Silver', minPoints: 0 },
    { name: 'Gold', minPoints: 500 },
    { name: 'Platinum', minPoints: 2000 },
  ],
};

export function getLoyaltyConfig(restaurant: Restaurant): LoyaltyConfig {
  const raw = ((restaurant.settings ?? {}) as Record<string, unknown>).loyalty as
    | Partial<LoyaltyConfig>
    | undefined;
  return {
    ...defaultLoyaltyConfig,
    ...(raw ?? {}),
    tiers: raw?.tiers && raw.tiers.length ? raw.tiers : defaultLoyaltyConfig.tiers,
  };
}

/** The customer's tier name for a given points balance (highest met). */
export function tierForPoints(points: number, tiers: LoyaltyTier[]): string {
  const sorted = [...tiers].sort((a, b) => a.minPoints - b.minPoints);
  let name = sorted[0]?.name ?? '—';
  for (const t of sorted) if (points >= t.minPoints) name = t.name;
  return name;
}

export async function saveLoyaltyConfig(restaurant: Restaurant, cfg: LoyaltyConfig): Promise<Restaurant> {
  const settings = { ...((restaurant.settings ?? {}) as Record<string, unknown>), loyalty: cfg };
  return updateRestaurant(restaurant.id, { settings });
}

export interface LoyaltyLedgerEntry {
  id: string;
  restaurantId: string;
  customerId: string;
  orderId: string | null;
  type: 'earn' | 'redeem' | 'adjust';
  points: number;
  note: string | null;
  createdAt: string;
}

export async function fetchLoyaltyLedger(restaurantId: string, customerId: string, limit = 50): Promise<LoyaltyLedgerEntry[]> {
  const { data, error } = await supabase
    .from('loyalty_ledger')
    .select('*')
    .eq('restaurantId', restaurantId)
    .eq('customerId', customerId)
    .order('createdAt', { ascending: false })
    .limit(limit);
  return need(data, error) as LoyaltyLedgerEntry[];
}

/** Redeem points: validates balance, writes a negative ledger row, decrements
    the customer's balance. Returns the new balance. */
export async function redeemLoyaltyPoints(
  restaurantId: string,
  customer: Customer,
  points: number,
  orderId?: string | null,
): Promise<number> {
  if (points <= 0) throw new Error('Points to redeem must be positive.');
  if (points > customer.loyaltyPoints) throw new Error('Not enough points.');
  const { error: lErr } = await supabase.from('loyalty_ledger').insert({
    restaurantId, customerId: customer.id, orderId: orderId ?? null,
    type: 'redeem', points: -points, note: 'Redeemed at billing',
  });
  if (lErr) throw lErr;
  const newBalance = customer.loyaltyPoints - points;
  const { error: cErr } = await supabase.from('customers')
    .update({ loyaltyPoints: newBalance }).eq('restaurantId', restaurantId).eq('id', customer.id);
  if (cErr) throw cErr;
  return newBalance;
}

/** Manual adjustment (+/-) with a note. Returns the new balance. */
export async function adjustLoyaltyPoints(
  restaurantId: string,
  customer: Customer,
  delta: number,
  note: string,
): Promise<number> {
  const newBalance = Math.max(0, customer.loyaltyPoints + delta);
  const { error: lErr } = await supabase.from('loyalty_ledger').insert({
    restaurantId, customerId: customer.id, orderId: null,
    type: 'adjust', points: delta, note,
  });
  if (lErr) throw lErr;
  const { error: cErr } = await supabase.from('customers')
    .update({ loyaltyPoints: newBalance }).eq('restaurantId', restaurantId).eq('id', customer.id);
  if (cErr) throw cErr;
  return newBalance;
}

/* ---------------- Virtual wallet / store credit ---------------- */

export interface WalletLedgerEntry {
  id: string;
  restaurantId: string;
  customerId: string;
  orderId: string | null;
  type: 'topup' | 'spend' | 'refund' | 'adjust';
  amount: number;
  note: string | null;
  createdAt: string;
}

/** Move money in/out of a customer's wallet. `amount` is signed (+credit/-debit).
    Returns the new balance. */
export async function adjustWallet(
  restaurantId: string,
  customer: Customer,
  amount: number,
  type: WalletLedgerEntry['type'],
  note?: string,
): Promise<number> {
  const current = customer.walletBalance ?? 0;
  if (amount < 0 && -amount > current) throw new Error('Insufficient wallet balance.');
  const newBalance = Math.max(0, current + amount);
  const { error: lErr } = await supabase.from('wallet_ledger').insert({
    restaurantId, customerId: customer.id, orderId: null, type, amount, note: note ?? null,
  });
  if (lErr) throw lErr;
  const { error: cErr } = await supabase.from('customers')
    .update({ walletBalance: newBalance }).eq('restaurantId', restaurantId).eq('id', customer.id);
  if (cErr) throw cErr;
  return newBalance;
}

export async function fetchWalletLedger(restaurantId: string, customerId: string, limit = 50): Promise<WalletLedgerEntry[]> {
  const { data, error } = await supabase.from('wallet_ledger').select('*')
    .eq('restaurantId', restaurantId).eq('customerId', customerId)
    .order('createdAt', { ascending: false }).limit(limit);
  return need(data, error) as WalletLedgerEntry[];
}

/* ---------------- Categories ---------------- */

export async function fetchCategories(restaurantId: string): Promise<Category[]> {
  const { data, error } = await supabase
    .from('categories').select('*').eq('restaurantId', restaurantId).order('sortOrder');
  return need(data, error) as Category[];
}

export async function addCategory(restaurantId: string, name: string, icon?: string): Promise<Category> {
  const { data: existing } = await supabase
    .from('categories').select('sortOrder').eq('restaurantId', restaurantId)
    .order('sortOrder', { ascending: false }).limit(1);
  const sortOrder = ((existing?.[0]?.sortOrder as number | undefined) ?? 0) + 1;
  const { data, error } = await supabase
    .from('categories')
    .insert({ id: makeId(name), restaurantId, name, icon: icon ?? null, sortOrder })
    .select().single();
  return need(data, error) as Category;
}

export async function updateCategory(restaurantId: string, id: string, patch: Partial<Pick<Category, 'name' | 'icon' | 'sortOrder'>>) {
  const { error } = await supabase.from('categories').update(patch)
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

export async function deleteCategory(restaurantId: string, id: string) {
  const { error } = await supabase.from('categories').delete()
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

/* ---------------- Menu items ---------------- */

export async function fetchMenu(restaurantId: string): Promise<MenuItem[]> {
  const { data, error } = await supabase
    .from('menu_items').select('*').eq('restaurantId', restaurantId).order('name');
  return need(data, error) as MenuItem[];
}

export async function upsertMenuItem(restaurantId: string, item: Partial<MenuItem> & { name: string }): Promise<MenuItem> {
  const row = { ...item, restaurantId, id: item.id ?? makeId(item.name) };
  const { data, error } = await supabase.from('menu_items').upsert(row).select().single();
  return need(data, error) as MenuItem;
}

export async function updateMenuItem(restaurantId: string, id: string, patch: Partial<MenuItem>) {
  const { error } = await supabase.from('menu_items').update(patch)
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

/** Bulk create/update menu items (CSV import). Each item keeps its `id` for an
    update, or gets a fresh one for a new row. Returns the saved rows. */
export async function bulkImportMenuItems(
  restaurantId: string,
  items: (Partial<MenuItem> & { name: string })[],
): Promise<MenuItem[]> {
  if (items.length === 0) return [];
  const rows = items.map((it) => ({ ...it, restaurantId, id: it.id ?? makeId(it.name) }));
  const { data, error } = await supabase.from('menu_items').upsert(rows).select();
  return need(data, error) as MenuItem[];
}

export async function deleteMenuItem(restaurantId: string, id: string) {
  const { error } = await supabase.from('menu_items').delete()
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

/* ---------------- Tables (floor + QR) ---------------- */

export async function fetchTables(restaurantId: string): Promise<RestaurantTable[]> {
  const { data, error } = await supabase
    .from('restaurant_tables').select('*').eq('restaurantId', restaurantId).order('id');
  return need(data, error) as RestaurantTable[];
}

export async function addTable(restaurantId: string, restaurantSlug: string, params: {
  id?: string; label: string; seats?: number; zone?: string;
}): Promise<RestaurantTable> {
  const id = params.id ?? makeId(params.label);
  const { data, error } = await supabase.from('restaurant_tables').insert({
    id,
    restaurantId,
    label: params.label,
    seats: params.seats ?? 4,
    zone: params.zone ?? 'Main Hall',
    slug: id,
    qrUrl: `/t/${restaurantSlug}/${id}`,
    orderingEnabled: true,
    status: 'free',
  }).select().single();
  return need(data, error) as RestaurantTable;
}

export async function updateTable(restaurantId: string, id: string, patch: Partial<RestaurantTable>) {
  const { error } = await supabase.from('restaurant_tables').update(patch)
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

export async function deleteTable(restaurantId: string, id: string) {
  const { error } = await supabase.from('restaurant_tables').delete()
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

/* ---------------- Orders ---------------- */

export async function fetchOrders(restaurantId: string, opts?: {
  from?: string;          // ISO timestamp (inclusive)
  to?: string;            // ISO timestamp (exclusive)
  status?: string;
  limit?: number;
}): Promise<Order[]> {
  let q = supabase.from('orders').select('*').eq('restaurantId', restaurantId)
    .order('placedAt', { ascending: false });
  if (opts?.from) q = q.gte('placedAt', opts.from);
  if (opts?.to) q = q.lt('placedAt', opts.to);
  if (opts?.status) q = q.eq('status', opts.status);
  if (opts?.limit) q = q.limit(opts.limit);
  const { data, error } = await q;
  return need(data, error) as Order[];
}

export async function fetchOrderItems(orderId: string): Promise<OrderItem[]> {
  const { data, error } = await supabase.from('order_items').select('*').eq('orderId', orderId);
  return need(data, error) as OrderItem[];
}

export async function updateOrder(restaurantId: string, id: string, patch: Partial<Order>) {
  const { error } = await supabase.from('orders').update(patch)
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

/** How long a voided bill can still be RESTORED from the recycle bin.
 *  A UI convenience only — after this the bill stays in the database (it must:
 *  CGST Sec 36 requires 72 months' retention for anything tied to an invoice,
 *  cancelled ones included) but drops out of the bin's default view. */
export const VOID_RESTORE_DAYS = 60;

/** Voided bills (the recycle bin): settled bills a manager voided on the POS.
 *  Hidden from the POS; reviewable and restorable here until they're purged
 *  ~VOID_RESTORE_DAYS after voidedAt. Newest void first. */
export async function fetchVoidedOrders(
  restaurantId: string,
  opts?: { from?: string; to?: string; limit?: number },
): Promise<Order[]> {
  let q = supabase
    .from('orders')
    .select('*')
    .eq('restaurantId', restaurantId)
    .eq('status', 'cancelled')
    .not('voidedAt', 'is', null)
    .order('voidedAt', { ascending: false });
  if (opts?.from) q = q.gte('voidedAt', opts.from);
  if (opts?.to) q = q.lt('voidedAt', opts.to);
  if (opts?.limit) q = q.limit(opts.limit);
  const { data, error } = await q;
  return need(data, error) as Order[];
}

/** Restore a voided bill back to a settled sale (keeps its invoice number).
 *  Goes through the restore_bill RPC so the void stamps are cleared atomically
 *  and the caller is pinned to their own restaurant server-side. */
export async function restoreVoidedOrder(orderId: string): Promise<void> {
  const { error } = await supabase.rpc('restore_bill', { p_order: orderId });
  if (error) throw error;
}

/** Order items in a window (for top-items / item-wise reports).
 *  Joined through orders so we can filter on placedAt. */
export async function fetchOrderItemsBetween(restaurantId: string, fromISO: string, toISO: string):
  Promise<(OrderItem & { orders: { placedAt: string } })[]> {
  const { data, error } = await supabase
    .from('order_items')
    .select('*, orders!inner(placedAt)')
    .eq('restaurantId', restaurantId)
    .gte('orders.placedAt', fromISO)
    .lt('orders.placedAt', toISO);
  return need(data, error) as (OrderItem & { orders: { placedAt: string } })[];
}

export interface NewOrderItem {
  name: string;
  qty: number;
  price: number;
  modifiers?: string[];
  specialInstructions?: string;
}

export async function placeOrder(params: {
  restaurantId: string;
  type?: string;
  tableId?: string | null;
  customer?: string;
  items: NewOrderItem[];
  subtotal: number;
  cgst: number;
  sgst: number;
  total: number;
  paymentMode?: string;
  paymentStatus?: string;
  customerPhone?: string | null;
}): Promise<Order> {
  const { restaurantId } = params;
  const { data: order, error } = await supabase
    .from('orders')
    .insert({
      restaurantId,
      type: params.type ?? 'Dine-in',
      tableId: params.tableId ?? null,
      customer: params.customer ?? 'Walk-in',
      customerPhone: params.customerPhone ?? null,
      itemCount: params.items.reduce((s, i) => s + i.qty, 0),
      subtotal: params.subtotal,
      cgst: params.cgst,
      sgst: params.sgst,
      total: params.total,
      status: 'received',
      paymentMode: params.paymentMode ?? 'UPI',
      paymentStatus: params.paymentStatus ?? 'pending',
    })
    .select()
    .single();
  if (error) throw error;

  const rows = params.items.map((i) => ({
    orderId: order.id,
    restaurantId,
    name: i.name,
    qty: i.qty,
    price: i.price,
    modifiers: i.modifiers ?? [],
    specialInstructions: i.specialInstructions ?? null,
  }));
  const { error: iErr } = await supabase.from('order_items').insert(rows);
  if (iErr) throw iErr;
  return order as Order;
}

/** Anonymous dine-in self-order. Prices/GST/total are recomputed SERVER-SIDE
    from menu_items (the browser can't set amounts or mark the order paid). The
    client sends only item ids/qty/variant/addons. Returns the new order id. */
export async function placeSelfOrder(params: {
  restaurantId: string;
  tableId?: string | null;
  items: { id: string; qty: number; variant?: string | null; addons?: string[]; specialInstructions?: string }[];
  /** Diner's name + phone captured on the welcome screen. Phone also drives loyalty. */
  customerName?: string | null;
  customerPhone?: string | null;
}): Promise<{ orderId: string; accessToken: string }> {
  const { data, error } = await supabase.rpc('place_self_order', {
    p_restaurant: params.restaurantId,
    p_table: params.tableId ?? null,
    p_items: params.items,
    p_customer: params.customerName?.trim() || null,
    p_phone: params.customerPhone?.trim() || null,
  });
  if (error) throw error;
  // The RPC returns a single row: { order_id, access_token }.
  const row = (Array.isArray(data) ? data[0] : data) as
    | { order_id: string; access_token: string }
    | undefined;
  if (!row?.order_id) throw new Error('Could not place the order.');
  return { orderId: row.order_id, accessToken: row.access_token };
}

/** Guest-facing live status for one order. Guests cannot read the orders table
    (RLS), so this goes through the token-gated order_status RPC. Returns null
    if the token doesn't match or the order is gone. */
export async function fetchGuestOrderStatus(
  orderId: string,
  accessToken: string,
): Promise<{ status: string; paymentStatus: string; total: number } | null> {
  const { data, error } = await supabase.rpc('order_status', {
    p_order: orderId,
    p_token: accessToken,
  });
  if (error || !data) return null;
  const row = (Array.isArray(data) ? data[0] : data) as
    | { status: string; paymentStatus: string; total: number }
    | undefined;
  if (!row) return null;
  return { status: row.status, paymentStatus: row.paymentStatus, total: Number(row.total) || 0 };
}

/** Live order feed (KDS / dashboard). Returns an unsubscribe fn. */
export function subscribeOrders(restaurantId: string, onChange: () => void): () => void {
  const channel = supabase
    .channel(`orders-${restaurantId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'ezydine', table: 'orders', filter: `restaurantId=eq.${restaurantId}` },
      onChange,
    )
    .subscribe();
  return () => { supabase.removeChannel(channel); };
}

/** Read the current status/total of a single order (customer self-order
    read-back). Returns null if not readable (e.g. RLS) or not found. */
export async function fetchOrderStatus(
  orderId: string,
): Promise<{ status: string; paymentStatus: string; total: number } | null> {
  const { data, error } = await supabase
    .from('orders')
    .select('status, paymentStatus, total')
    .eq('id', orderId)
    .maybeSingle();
  if (error || !data) return null;
  return data as { status: string; paymentStatus: string; total: number };
}

/** Live status for ONE order (customer tracking screen). Fires onChange with
    the new status whenever that order row changes. Returns an unsubscribe fn. */
export function subscribeOrderStatus(
  orderId: string,
  onChange: (status: string) => void,
): () => void {
  const channel = supabase
    .channel(`order-${orderId}`)
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'ezydine', table: 'orders', filter: `id=eq.${orderId}` },
      (payload) => {
        const next = (payload.new as { status?: string } | null)?.status;
        if (next) onChange(next);
      },
    )
    .subscribe();
  return () => { supabase.removeChannel(channel); };
}

/* ---------------- Customers ---------------- */

export async function fetchCustomers(restaurantId: string): Promise<Customer[]> {
  const { data, error } = await supabase
    .from('customers').select('*').eq('restaurantId', restaurantId).order('name');
  return need(data, error) as Customer[];
}

export async function upsertCustomer(restaurantId: string, customer: Partial<Customer> & { name: string }): Promise<Customer> {
  const row = { ...customer, restaurantId };
  const { data, error } = row.id
    ? await supabase.from('customers').update(row).eq('id', row.id).select().single()
    : await supabase.from('customers').insert(row).select().single();
  return need(data, error) as Customer;
}

export async function deleteCustomer(restaurantId: string, id: string) {
  const { error } = await supabase.from('customers').delete()
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

/* ---------------- Staff ---------------- */

export async function fetchStaff(restaurantId: string): Promise<StaffMember[]> {
  const { data, error } = await supabase
    .from('staff').select('*').eq('restaurantId', restaurantId).order('name');
  return need(data, error) as StaffMember[];
}

export async function upsertStaff(restaurantId: string, member: Partial<StaffMember> & { name: string; role: string }): Promise<StaffMember> {
  // Only write columns that actually exist on ezydine.staff. The plaintext
  // `pin` is intentionally NOT written here — PINs are stored as a bcrypt hash
  // via the set_staff_pin RPC (setStaffPin). Writing `pin` broke staff saves
  // after the security cutover dropped that column (400: column not found).
  const allowed = ['name', 'role', 'phone', 'email', 'shift', 'status', 'permissions', 'joinedDate', 'active'] as const;
  const row: Record<string, unknown> = { restaurantId };
  for (const k of allowed) {
    const v = (member as Record<string, unknown>)[k];
    if (v !== undefined) row[k] = v;
  }
  const { data, error } = member.id
    ? await supabase.from('staff').update(row).eq('id', member.id).eq('restaurantId', restaurantId).select().single()
    : await supabase.from('staff').insert(row).select().single();
  return need(data, error) as StaffMember;
}

/** Store a bcrypt hash of a staff PIN server-side (POS/Captain verify via the
    verify_pin RPC). The RPC also enforces per-restaurant PIN uniqueness. */
export async function setStaffPin(staffId: string, pin: string) {
  const { error } = await supabase.rpc('set_staff_pin', { p_staff: staffId, p_pin: pin });
  if (error) throw error;
}

/** True if `pin` is already taken (owner master PIN or another staff) in this
    restaurant. Pass the id of the staff being edited as `excludeStaffId` so a
    member keeping their own PIN isn't flagged. Used as a pre-save guard. */
export async function pinInUse(pin: string, excludeStaffId?: string | null): Promise<boolean> {
  const { data, error } = await supabase.rpc('pin_in_use', {
    p_pin: pin,
    p_exclude: excludeStaffId ?? null,
  });
  if (error) throw error;
  return data === true;
}

/** Store a bcrypt hash of the owner master PIN. */
export async function setOwnerPinHash(pin: string) {
  const { error } = await supabase.rpc('set_owner_pin', { p_pin: pin });
  if (error) throw error;
}

/* ---------------- Multi-outlet ---------------- */

export interface OutletRef { id: string; name: string; slug: string; }
export interface ConsolidatedRow { restaurantId: string; name: string; orders: number; gross: number; }

/** Outlets the signed-in owner can access (primary + linked memberships). */
export async function listMyOutlets(): Promise<OutletRef[]> {
  const { data, error } = await supabase.rpc('list_my_outlets');
  if (error) throw error;
  return (data ?? []) as OutletRef[];
}

/** Consolidated sales per outlet for a window (caller's outlets only). */
export async function fetchConsolidatedSales(fromISO: string, toISO: string): Promise<ConsolidatedRow[]> {
  const { data, error } = await supabase.rpc('consolidated_sales', { p_from: fromISO, p_to: toISO });
  if (error) throw error;
  return (data ?? []).map((r: { restaurantId: string; name: string; orders: number; gross: number }) => ({
    restaurantId: r.restaurantId, name: r.name, orders: Number(r.orders), gross: Number(r.gross),
  }));
}

/* ---------------- WhatsApp marketing campaigns ---------------- */

export interface Campaign {
  id: string;
  restaurantId: string;
  name: string;
  templateName: string;
  bodyParam: string | null;
  segment: string;
  status: 'draft' | 'sending' | 'sent' | 'failed';
  audienceCount: number;
  sentCount: number;
  failedCount: number;
  createdAt: string;
  sentAt: string | null;
}

export async function listCampaigns(restaurantId: string): Promise<Campaign[]> {
  const { data, error } = await supabase.from('campaigns').select('*')
    .eq('restaurantId', restaurantId).order('createdAt', { ascending: false });
  return need(data, error) as Campaign[];
}

export async function createCampaign(restaurantId: string, c: {
  name: string; templateName: string; bodyParam?: string; segment?: string;
}): Promise<Campaign> {
  const { data, error } = await supabase.from('campaigns').insert({
    restaurantId, name: c.name, templateName: c.templateName,
    bodyParam: c.bodyParam ?? null, segment: c.segment ?? 'All',
  }).select().single();
  return need(data, error) as Campaign;
}

export async function deleteCampaign(restaurantId: string, id: string) {
  const { error } = await supabase.from('campaigns').delete()
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

/** Trigger a campaign send via the send-campaign edge function. */
export async function sendCampaign(campaignId: string): Promise<{ sent: number; failed: number; audience: number }> {
  const { data, error } = await supabase.functions.invoke('send-campaign', { body: { campaignId } });
  if (error) throw error;
  const d = data as { sent?: number; failed?: number; audience?: number; error?: string };
  if (d.error) throw new Error(d.error);
  return { sent: d.sent ?? 0, failed: d.failed ?? 0, audience: d.audience ?? 0 };
}

/** Count customers in a segment who have a phone (campaign audience preview). */
export async function countSegmentAudience(restaurantId: string, segment: string): Promise<number> {
  let q = supabase.from('customers').select('id', { count: 'exact', head: true })
    .eq('restaurantId', restaurantId).not('phone', 'is', null);
  if (segment && segment !== 'All') q = q.eq('segment', segment);
  const { count, error } = await q;
  if (error) throw error;
  return count ?? 0;
}

/* ---------------- E-Invoice (GST IRN) ---------------- */

export interface EInvoiceConfig {
  enabled: boolean;
  legalName: string;
  address: string;
  city: string;
  pincode: string;
  stateCode: string;   // 2-digit GST state code
}

export const defaultEInvoiceConfig: EInvoiceConfig = {
  enabled: false, legalName: '', address: '', city: '', pincode: '', stateCode: '',
};

export function getEInvoiceConfig(restaurant: Restaurant): EInvoiceConfig {
  const raw = ((restaurant.settings ?? {}) as Record<string, unknown>).einvoice as Partial<EInvoiceConfig> | undefined;
  return { ...defaultEInvoiceConfig, ...(raw ?? {}) };
}

export async function saveEInvoiceConfig(restaurant: Restaurant, cfg: EInvoiceConfig): Promise<Restaurant> {
  const settings = { ...((restaurant.settings ?? {}) as Record<string, unknown>), einvoice: cfg };
  return updateRestaurant(restaurant.id, { settings });
}

/** Generate an IRN for an order via the generate-einvoice edge function.
    Returns { irn, status } or throws with the GSP error. */
export async function generateEInvoice(orderId: string): Promise<{ irn?: string; status: string }> {
  const { data, error } = await supabase.functions.invoke('generate-einvoice', { body: { orderId } });
  if (error) throw error;
  const d = data as { irn?: string; status?: string; error?: string };
  if (d.error) throw new Error(d.error);
  return { irn: d.irn, status: d.status ?? 'generated' };
}

/* ---------------- Reservations (TRM) ---------------- */

export interface Reservation {
  id: string;
  restaurantId: string;
  customerName: string;
  phone: string | null;
  partySize: number;
  reservedAt: string;        // ISO timestamp
  durationMins: number;
  tableId: string | null;
  status: 'booked' | 'seated' | 'completed' | 'cancelled' | 'no_show';
  notes: string | null;
  createdAt: string;
}

export async function fetchReservations(restaurantId: string, opts?: { from?: string; to?: string }): Promise<Reservation[]> {
  let q = supabase.from('reservations').select('*').eq('restaurantId', restaurantId)
    .order('reservedAt', { ascending: true });
  if (opts?.from) q = q.gte('reservedAt', opts.from);
  if (opts?.to) q = q.lt('reservedAt', opts.to);
  const { data, error } = await q;
  return need(data, error) as Reservation[];
}

export async function createReservation(
  restaurantId: string,
  r: Omit<Reservation, 'id' | 'restaurantId' | 'createdAt' | 'status'> & { status?: Reservation['status'] },
): Promise<Reservation> {
  const { data, error } = await supabase.from('reservations').insert({
    restaurantId,
    customerName: r.customerName,
    phone: r.phone ?? null,
    partySize: r.partySize,
    reservedAt: r.reservedAt,
    durationMins: r.durationMins,
    tableId: r.tableId ?? null,
    status: r.status ?? 'booked',
    notes: r.notes ?? null,
  }).select().single();
  return need(data, error) as Reservation;
}

export async function updateReservation(restaurantId: string, id: string, patch: Partial<Reservation>) {
  const { error } = await supabase.from('reservations').update(patch)
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

export async function deleteReservation(restaurantId: string, id: string) {
  const { error } = await supabase.from('reservations').delete()
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

/* ---------------- Tokens (QSR pickup numbers) ---------------- */

/** Atomically allocate the next pickup token for today (resets per day). */
export async function nextToken(restaurantId: string): Promise<number> {
  const { data, error } = await supabase.rpc('next_token', { p_restaurant: restaurantId });
  if (error) throw error;
  return data as number;
}

/** Today's orders that have been given a pickup token (for the token board). */
export async function fetchTodayTokens(restaurantId: string): Promise<Order[]> {
  const start = new Date(); start.setHours(0, 0, 0, 0);
  const { data, error } = await supabase.from('orders').select('*')
    .eq('restaurantId', restaurantId)
    .gte('placedAt', start.toISOString())
    .not('tokenNumber', 'is', null)
    .order('tokenNumber', { ascending: true });
  return need(data, error) as Order[];
}

export async function deleteStaff(restaurantId: string, id: string) {
  const { error } = await supabase.from('staff').delete()
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

/* ---------------- Audit log (POS activity trail) ---------------- */

export async function fetchAudit(restaurantId: string, limit = 100): Promise<AuditEntry[]> {
  const { data, error } = await supabase
    .from('audit_log').select('*')
    .eq('restaurantId', restaurantId)
    .order('at', { ascending: false })
    .limit(limit);
  return need(data, error) as AuditEntry[];
}

/* ---------------- Inventory ---------------- */

export async function fetchInventory(restaurantId: string): Promise<InventoryItem[]> {
  const { data, error } = await supabase
    .from('inventory_items').select('*').eq('restaurantId', restaurantId).order('name');
  return need(data, error) as InventoryItem[];
}

export async function upsertInventoryItem(restaurantId: string, item: Partial<InventoryItem> & { name: string }): Promise<InventoryItem> {
  const row = { ...item, restaurantId };
  const { data, error } = row.id
    ? await supabase.from('inventory_items').update(row).eq('id', row.id).select().single()
    : await supabase.from('inventory_items').insert(row).select().single();
  return need(data, error) as InventoryItem;
}

export async function deleteInventoryItem(restaurantId: string, id: string) {
  const { error } = await supabase.from('inventory_items').delete()
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

export async function fetchWastage(restaurantId: string): Promise<WastageEntry[]> {
  const { data, error } = await supabase
    .from('wastage_entries').select('*').eq('restaurantId', restaurantId)
    .order('recordedAt', { ascending: false });
  return need(data, error) as WastageEntry[];
}

export async function addWastage(restaurantId: string, entry: Omit<WastageEntry, 'id' | 'restaurantId' | 'recordedAt'>): Promise<WastageEntry> {
  const { data, error } = await supabase
    .from('wastage_entries').insert({ ...entry, restaurantId }).select().single();
  return need(data, error) as WastageEntry;
}

/* ---------------- Recipes (bill of materials) ---------------- */

export interface RecipeItem {
  id: string;
  restaurantId: string;
  menuItemId: string;
  menuItemName: string | null;
  inventoryItemId: string;
  qtyPerUnit: number;
  unit: string | null;
}

export async function fetchRecipe(restaurantId: string, menuItemId: string): Promise<RecipeItem[]> {
  const { data, error } = await supabase
    .from('recipe_items').select('*')
    .eq('restaurantId', restaurantId).eq('menuItemId', menuItemId);
  return need(data, error) as RecipeItem[];
}

// Which menu items already have a recipe defined (for the "has recipe" badge).
export async function fetchRecipedMenuIds(restaurantId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('recipe_items').select('menuItemId').eq('restaurantId', restaurantId);
  if (error) throw error;
  return Array.from(new Set((data ?? []).map((r: { menuItemId: string }) => r.menuItemId)));
}

// Replace the whole recipe for a menu item in one shot.
export async function saveRecipe(
  restaurantId: string,
  menuItemId: string,
  menuItemName: string,
  lines: { inventoryItemId: string; qtyPerUnit: number; unit: string | null }[],
): Promise<void> {
  const del = await supabase.from('recipe_items').delete()
    .eq('restaurantId', restaurantId).eq('menuItemId', menuItemId);
  if (del.error) throw del.error;
  const rows = lines
    .filter((l) => l.inventoryItemId && l.qtyPerUnit > 0)
    .map((l) => ({ restaurantId, menuItemId, menuItemName, ...l }));
  if (rows.length === 0) return;
  const { error } = await supabase.from('recipe_items').insert(rows);
  if (error) throw error;
}

/* ---------------- Purchases (purchase orders) ---------------- */

export interface Purchase {
  id: string;
  restaurantId: string;
  poNumber: string | null;
  supplier: string | null;
  status: 'draft' | 'ordered' | 'received' | 'cancelled';
  total: number;
  note: string | null;
  orderedAt: string | null;
  receivedAt: string | null;
  createdAt: string;
}

export interface PurchaseItem {
  id: string;
  restaurantId: string;
  purchaseId: string;
  inventoryItemId: string;
  itemName: string | null;
  qty: number;
  unitCost: number;
}

export async function fetchPurchases(restaurantId: string): Promise<Purchase[]> {
  const { data, error } = await supabase
    .from('purchases').select('*')
    .eq('restaurantId', restaurantId).order('createdAt', { ascending: false });
  return need(data, error) as Purchase[];
}

export async function fetchPurchaseItems(restaurantId: string, purchaseId: string): Promise<PurchaseItem[]> {
  const { data, error } = await supabase
    .from('purchase_items').select('*')
    .eq('restaurantId', restaurantId).eq('purchaseId', purchaseId);
  return need(data, error) as PurchaseItem[];
}

// Create a PO with its lines. status 'received' triggers stock-in server-side.
export async function createPurchase(
  restaurantId: string,
  header: { poNumber?: string; supplier?: string; note?: string; status?: Purchase['status'] },
  lines: { inventoryItemId: string; itemName: string; qty: number; unitCost: number }[],
): Promise<Purchase> {
  const total = lines.reduce((s, l) => s + l.qty * l.unitCost, 0);
  const status = header.status ?? 'received';
  const { data, error } = await supabase.from('purchases').insert({
    restaurantId,
    poNumber: header.poNumber ?? null,
    supplier: header.supplier ?? null,
    note: header.note ?? null,
    status,
    total,
    orderedAt: new Date().toISOString(),
    receivedAt: status === 'received' ? new Date().toISOString() : null,
  }).select().single();
  const po = need(data, error) as Purchase;
  const rows = lines
    .filter((l) => l.inventoryItemId && l.qty > 0)
    .map((l) => ({ restaurantId, purchaseId: po.id, ...l }));
  if (rows.length) {
    const ins = await supabase.from('purchase_items').insert(rows);
    if (ins.error) throw ins.error;
  }
  return po;
}

// Mark a draft/ordered PO as received -> server trigger adds stock.
export async function receivePurchase(restaurantId: string, purchaseId: string): Promise<void> {
  const { error } = await supabase.from('purchases')
    .update({ status: 'received', receivedAt: new Date().toISOString() })
    .eq('restaurantId', restaurantId).eq('id', purchaseId);
  if (error) throw error;
}

/* ---------------- Inventory consumption / variance ---------------- */

export interface ConsumptionRow {
  inventoryItemId: string;
  name: string;
  unit: string | null;
  purchased: number;
  consumed: number;
  wasted: number;
  currentStock: number;
  reorderPoint: number;
  unitCost: number;
  stockValue: number;
}

export async function fetchConsumption(from: string, to: string): Promise<ConsumptionRow[]> {
  const { data, error } = await supabase.rpc('inventory_consumption', { p_from: from, p_to: to });
  return need(data, error) as ConsumptionRow[];
}

/* ---------------- Public (QR self-order) ---------------- */

export async function fetchMenuBySlug(slug: string) {
  // Anon path: read only safe restaurant columns via a security-definer RPC
  // (the restaurants table is no longer anon-readable, so ownerPin etc. can't leak).
  const { data: rows, error: rErr } = await supabase.rpc('restaurant_public', { p_slug: slug });
  if (rErr) throw rErr;
  const r = Array.isArray(rows) ? rows[0] : rows;
  if (!r) throw new Error('Restaurant not found');
  const [categories, menu, tables] = await Promise.all([
    fetchCategories(r.id),
    fetchMenu(r.id),
    fetchTables(r.id),
  ]);
  return {
    restaurant: r as Pick<Restaurant, 'id' | 'name' | 'slug' | 'gstRate' | 'gstInclusive'> & { channels?: Record<string, boolean> | null },
    categories,
    menu,
    tables,
  };
}

/** Public gate: can this restaurant (by slug) take online QR / self-orders?
    True only when the plan reaches Growth+ and isn't blocked. Anonymous-safe
    (SECURITY DEFINER RPC). On error/absence we default to false (browse-only). */
export async function fetchQrOrderingEnabled(slug: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('restaurant_qr_enabled', { p_slug: slug });
  if (error) return false;
  return data === true;
}

/* ---------------- Public plan catalog (marketing + paywall) ---------------- */

export interface PublicPlan {
  tier: string;
  label: string;
  price: number;
  /** Original/list price; when > price, show it struck-through. */
  mrp: number | null;
  period: string;
  rank: number;
  features: string[];
}

/** Live subscription plans (active only, ordered by rank) for the public site
    and the in-app paywall. Anon-readable via the public_plans() RPC. Returns []
    on any error so callers can fall back to the static catalog. */
export async function fetchPublicPlans(): Promise<PublicPlan[]> {
  try {
    const { data, error } = await supabase.rpc('public_plans');
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map((r) => ({
      tier: String(r.tier),
      label: String(r.label),
      price: Number(r.price) || 0,
      mrp: r.mrp == null ? null : Number(r.mrp),
      period: String(r.period ?? 'year'),
      rank: Number(r.rank) || 0,
      features: Array.isArray(r.features) ? (r.features as string[]) : [],
    }));
  } catch {
    return [];
  }
}

/** Resolve a public slug → safe restaurant fields (anon, no Firebase). */
export async function fetchRestaurantPublic(slug: string): Promise<Pick<Restaurant, 'id' | 'name' | 'slug' | 'gstRate' | 'gstInclusive'>> {
  const { data: rows, error } = await supabase.rpc('restaurant_public', { p_slug: slug });
  if (error) throw error;
  const r = Array.isArray(rows) ? rows[0] : rows;
  if (!r) throw new Error('Restaurant not found');
  return r as Pick<Restaurant, 'id' | 'name' | 'slug' | 'gstRate' | 'gstInclusive'>;
}

/* ---------------- Feedback ---------------- */

export interface Feedback {
  id: string;
  restaurantId: string;
  orderId: string | null;
  rating: number;
  comment: string | null;
  customerName: string | null;
  phone: string | null;
  source: string;
  createdAt: string;
}

/** Anonymous feedback submission (closed RPC). Returns the new feedback id. */
export async function submitFeedback(params: {
  restaurantId: string;
  rating: number;
  comment?: string;
  name?: string;
  phone?: string;
  source?: 'qr' | 'link';
}): Promise<string> {
  const { data, error } = await supabase.rpc('submit_feedback', {
    p_restaurant: params.restaurantId,
    p_rating: params.rating,
    p_comment: params.comment ?? null,
    p_name: params.name ?? null,
    p_phone: params.phone ?? null,
    p_source: params.source ?? 'qr',
  });
  if (error) throw error;
  return data as string;
}

export async function fetchFeedback(restaurantId: string, limit = 200): Promise<Feedback[]> {
  const { data, error } = await supabase
    .from('feedback')
    .select('*')
    .eq('restaurantId', restaurantId)
    .order('createdAt', { ascending: false })
    .limit(limit);
  return need(data, error) as Feedback[];
}

/* ---------------- Aggregator outlets (Swiggy / Zomato / ONDC) ---------------- */

export interface AggregatorOutlet {
  id: string;
  channel: string;            // 'Swiggy' | 'Zomato' | 'ONDC'
  externalStoreId: string;
  restaurantId: string;
  active: boolean;
  createdAt: string;
}

export async function listAggregatorOutlets(restaurantId: string): Promise<AggregatorOutlet[]> {
  const { data, error } = await supabase
    .from('aggregator_outlets').select('*').eq('restaurantId', restaurantId).order('channel');
  return need(data, error) as AggregatorOutlet[];
}

export async function createAggregatorOutlet(restaurantId: string, channel: string, externalStoreId: string): Promise<AggregatorOutlet> {
  const { data, error } = await supabase.from('aggregator_outlets')
    .insert({ restaurantId, channel, externalStoreId, active: true }).select().single();
  return need(data, error) as AggregatorOutlet;
}

export async function updateAggregatorOutlet(restaurantId: string, id: string, patch: Partial<Pick<AggregatorOutlet, 'externalStoreId' | 'active'>>) {
  const { error } = await supabase.from('aggregator_outlets').update(patch)
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

export async function deleteAggregatorOutlet(restaurantId: string, id: string) {
  const { error } = await supabase.from('aggregator_outlets').delete()
    .eq('restaurantId', restaurantId).eq('id', id);
  if (error) throw error;
}

export interface ChannelRecon {
  channel: string;
  orders: number;
  gross: number;
}

/** Reconciliation summary: aggregator orders grouped by channel for a window.
    Merchants compare gross + estimated commission against the aggregator's
    payout statement. */
export async function fetchAggregatorRecon(restaurantId: string, fromISO: string, toISO: string): Promise<ChannelRecon[]> {
  const { data, error } = await supabase.from('orders')
    .select('type, total, placedAt')
    .eq('restaurantId', restaurantId)
    .gte('placedAt', fromISO).lt('placedAt', toISO)
    .in('type', ['Swiggy', 'Zomato', 'ONDC']);
  if (error) throw error;
  const map = new Map<string, ChannelRecon>();
  for (const row of (data ?? []) as { type: string; total: number }[]) {
    const c = map.get(row.type) ?? { channel: row.type, orders: 0, gross: 0 };
    c.orders += 1;
    c.gross += Number(row.total) || 0;
    map.set(row.type, c);
  }
  return [...map.values()].sort((a, b) => b.gross - a.gross);
}

/* ---------------- Subscription / access ---------------- */

import type { EffectiveTier, Tier } from './access';

/** One-row result of ezydine.my_access() — the caller's subscription state. */
export interface Access {
  status: 'trialing' | 'active' | 'expired' | 'cancelled';
  planTier: Tier | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  effectiveTier: EffectiveTier;
  daysLeft: number;
  blocked: boolean;
}

/** Treat a missing/empty subscription row as a hard block (trial never started
    or fully expired) so the app fails closed rather than open. */
const BLOCKED_ACCESS: Access = {
  status: 'expired',
  planTier: null,
  trialEndsAt: null,
  currentPeriodEnd: null,
  effectiveTier: 'none',
  daysLeft: 0,
  blocked: true,
};

export async function fetchMyAccess(): Promise<Access> {
  const { data, error } = await supabase.rpc('my_access');
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return BLOCKED_ACCESS;
  return {
    status: row.status,
    planTier: row.planTier ?? null,
    trialEndsAt: row.trialEndsAt ?? null,
    currentPeriodEnd: row.currentPeriodEnd ?? null,
    effectiveTier: (row.effectiveTier ?? 'none') as EffectiveTier,
    daysLeft: Number(row.daysLeft) || 0,
    blocked: !!row.blocked,
  };
}

/** One row of ezydine.my_features() — superadmin-editable feature gating. */
export interface MyFeatureRow {
  feature: string;
  minTier: string | null;
  enabled: boolean;
  overrideEnabled: boolean | null;
}

/** Fetch the caller's effective feature matrix. Empty array on error/absence
    so the app falls back to the hardcoded FEATURE_TIER defaults. */
export async function fetchMyFeatures(): Promise<MyFeatureRow[]> {
  const { data, error } = await supabase.rpc('my_features');
  if (error || !data) return [];
  return (data as Record<string, unknown>[]).map((r) => ({
    feature: String(r.feature),
    minTier: (r.minTier as string | null) ?? null,
    enabled: r.enabled !== false,
    overrideEnabled: (r.overrideEnabled as boolean | null) ?? null,
  }));
}

/** A platform announcement shown as a banner in the merchant dashboard. */
export interface ActiveAnnouncement {
  id: string;
  title: string;
  body: string;
  level: 'info' | 'warning' | 'critical';
  audience: string;
}

/** Fetch active announcements (audience filtered client-side). */
export async function fetchActiveAnnouncements(): Promise<ActiveAnnouncement[]> {
  const { data, error } = await supabase.rpc('active_announcements');
  if (error || !data) return [];
  return (data as Record<string, unknown>[]).map((r) => ({
    id: String(r.id),
    title: String(r.title),
    body: String(r.body),
    level: (r.level as ActiveAnnouncement['level']) ?? 'info',
    audience: String(r.audience ?? 'all'),
  }));
}
